import { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Empty, Form, Input, Modal, Radio, Spin, Tag, message } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { CityAsyncSelect } from '@/components/pos/CityAsyncSelect';
import { WardAsyncSelect } from '@/components/pos/WardAsyncSelect';
import {
  useAddCustomerAddress,
  useCustomerAddresses,
  useSetDefaultCustomerAddress,
  useUpdateCustomerAddress,
} from '@/hooks/useCustomers';
import { AddressReceiversField } from '@/pages/Customers/parts/AddressReceiversField';
import {
  entryCity,
  entryWard,
  filterAddressEntries,
  formatEntryAddress,
  readAddressLocationId,
  type AddressBookEntry,
} from './addressBook';

/** Khớp regex của `CustomerAddressService.addAddress` ở BE — sổ chỉ nhận SĐT di động. */
const BOOK_PHONE_PATTERN = /^(0|\+84)[3-9]\d{8}$/;

interface Props {
  open: boolean;
  customerId: string;
  customerName?: string;
  /** Entry đang dùng trên form giao nhận — chọn sẵn khi mở popup. */
  currentAddressId?: string;
  /** `customerEditAbilities(...).canEditDeliveryAddress` — thiếu thì chỉ chọn & áp dụng. */
  canManage: boolean;
  onApply: (entry: AddressBookEntry) => void;
  onClose: () => void;
}

interface EntryFormValues {
  /** TCR-2384 — Tên địa chỉ giao hàng. */
  address_name?: string;
  name: string;
  phone?: string;
  email?: string;
  city_id: string;
  ward_id: string;
  address: string;
  /** TCR-2384 */
  note?: string;
  receivers?: { name?: string; phone?: string; note?: string }[];
  is_default?: boolean;
}

type ReceiverBody = { name: string; phone: string; note?: string };

/** Chuẩn hoá danh sách người nhận: trim, bỏ hàng trống, bỏ `note` rỗng (khớp dạng BE lưu). */
function normalizeReceivers(rows: EntryFormValues['receivers'] | AddressBookEntry['receivers']): ReceiverBody[] {
  return (rows ?? [])
    .map((r) => ({ name: (r?.name ?? '').trim(), phone: (r?.phone ?? '').trim(), note: (r?.note ?? '').trim() }))
    .filter((r) => r.name || r.phone || r.note)
    .map((r) => (r.note ? r : { name: r.name, phone: r.phone }));
}

/**
 * TCR-2128 — popup "Sổ địa chỉ" của tab Thông tin giao nhận (dialog Thông tin khách hàng, POS).
 *
 * Form thêm/sửa nằm NGAY TRONG popup (theo mockup) thay vì Drawer: thu ngân đang ở giữa một
 * dialog khác, mở thêm Drawer thứ ba là lạc ngữ cảnh. Quản lý đầy đủ (xoá…) vẫn ở tab Địa chỉ
 * của màn chi tiết khách (`Customers/parts/AddressesTab.tsx`).
 */
export function AddressBookModal({
  open,
  customerId,
  customerName,
  currentAddressId,
  canManage,
  onApply,
  onClose,
}: Props) {
  const { data, isLoading } = useCustomerAddresses(open ? customerId : undefined);
  const list = useMemo(() => (data ?? []) as unknown as AddressBookEntry[], [data]);
  const addMut = useAddCustomerAddress(customerId);
  const updateMut = useUpdateCustomerAddress(customerId);
  const setDefaultMut = useSetDefaultCustomerAddress(customerId);

  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | undefined>(currentAddressId);
  /** `null` = đang thêm mới, entry = đang sửa, `undefined` = đang xem danh sách. */
  const [editing, setEditing] = useState<AddressBookEntry | null | undefined>(undefined);
  const [entryForm] = Form.useForm<EntryFormValues>();
  const editCityId = Form.useWatch('city_id', entryForm);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setEditing(undefined);
    setSelectedId(currentAddressId);
  }, [open, currentAddressId]);

  const visible = filterAddressEntries(list, query);
  const selected = list.find((e) => e.address_id === selectedId);

  const startEdit = (entry: AddressBookEntry | null) => {
    setEditing(entry);
    entryForm.resetFields();
    if (entry) {
      entryForm.setFieldsValue({
        address_name: entry.address_name ?? '',
        name: entry.name ?? '',
        phone: entry.phone ?? '',
        email: entry.email ?? '',
        city_id: readAddressLocationId(entryCity(entry)) || undefined,
        ward_id: readAddressLocationId(entryWard(entry)) || undefined,
        address: entry.address ?? '',
        note: entry.note ?? '',
        receivers: (entry.receivers ?? []).map((r) => ({ name: r.name, phone: r.phone, note: r.note ?? '' })),
        is_default: Boolean(entry.is_default),
      });
    } else {
      // Sổ rỗng thì địa chỉ đầu tiên luôn là mặc định (BE cũng ép như vậy).
      entryForm.setFieldsValue({ is_default: list.length === 0 });
    }
  };

  const saveEntry = async () => {
    const v = await entryForm.validateFields();
    const body: Record<string, unknown> = {
      name: v.name.trim(),
      city_id: v.city_id,
      ward_id: v.ward_id,
      address: v.address.trim(),
    };
    if (v.phone?.trim()) body.phone = v.phone.trim();
    if (v.email?.trim()) body.email = v.email.trim();
    // TCR-2384: BE giữ nguyên field vắng mặt trong body, `receivers: []` / `note: ''` là xoá —
    // nên khi SỬA chỉ gửi 2 key này nếu giá trị thật sự khác entry gốc.
    const note = (v.note ?? '').trim();
    const addressName = (v.address_name ?? '').trim();
    const receivers = normalizeReceivers(v.receivers);
    if (editing) {
      if (note !== (editing.note ?? '')) body.note = note;
      if (addressName !== (editing.address_name ?? '')) body.address_name = addressName;
      if (JSON.stringify(receivers) !== JSON.stringify(normalizeReceivers(editing.receivers))) {
        body.receivers = receivers;
      }
    } else {
      if (note) body.note = note;
      if (addressName) body.address_name = addressName;
      if (receivers.length > 0) body.receivers = receivers;
    }
    // `updateAddress` ở BE `Object.assign` nguyên body: gửi `false` cho entry đang mặc định là
    // xoá mất mặc định của khách — chỉ gửi khi bật.
    if (v.is_default) body.is_default = true;
    try {
      if (editing) {
        await updateMut.mutateAsync({ addressId: editing.address_id, body });
        setSelectedId(editing.address_id);
        message.success('Đã cập nhật địa chỉ');
      } else {
        const res = await addMut.mutateAsync(body);
        const newId = (res.data as unknown as { address_id?: string; _id?: string } | undefined);
        setSelectedId(newId?.address_id ?? newId?._id);
        message.success('Đã thêm địa chỉ vào sổ');
      }
      setEditing(undefined);
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message;
      message.error(msg || 'Không lưu được địa chỉ');
    }
  };

  const setDefault = async (entry: AddressBookEntry) => {
    try {
      await setDefaultMut.mutateAsync(entry.address_id);
      message.success('Đã đặt làm địa chỉ mặc định');
    } catch {
      message.error('Không đặt được địa chỉ mặc định');
    }
  };

  const inForm = editing !== undefined;

  return (
    <Modal
      open={open}
      title={`Sổ địa chỉ${customerName ? ` — ${customerName}` : ''}`}
      width={680}
      onCancel={onClose}
      destroyOnClose
      footer={
        inForm
          ? [
              <Button key="back" onClick={() => setEditing(undefined)}>
                Quay lại
              </Button>,
              <Button
                key="save"
                type="primary"
                loading={addMut.isPending || updateMut.isPending}
                onClick={saveEntry}
              >
                Lưu địa chỉ
              </Button>,
            ]
          : [
              <Button key="cancel" onClick={onClose}>
                Huỷ
              </Button>,
              <Button
                key="apply"
                type="primary"
                disabled={!selected}
                onClick={() => selected && onApply(selected)}
              >
                Áp dụng
              </Button>,
            ]
      }
    >
      {inForm ? (
        <Form form={entryForm} layout="vertical" requiredMark className="addr-book-form">
          <div className="addr-book-form-title">{editing ? 'Sửa địa chỉ' : 'Thêm địa chỉ mới'}</div>
          <Form.Item
            name="address_name"
            label="Tên địa chỉ giao hàng"
            rules={[{ max: 256, message: 'Tối đa 256 ký tự' }]}
          >
            <Input maxLength={256} placeholder="VD: FujiMart Ngọc Lâm" />
          </Form.Item>
          <div className="addr-book-form-grid">
            <Form.Item
              name="name"
              label="Họ tên người nhận"
              rules={[
                { required: true, whitespace: true, message: 'Vui lòng nhập họ tên' },
                { min: 3, max: 50, message: 'Họ tên 3–50 ký tự' },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="phone"
              label="Số điện thoại"
              rules={[
                { required: true, message: 'Vui lòng nhập SĐT' },
                { pattern: BOOK_PHONE_PATTERN, message: 'SĐT di động không hợp lệ' },
              ]}
            >
              <Input maxLength={12} />
            </Form.Item>
            <Form.Item
              name="city_id"
              label="Thành phố"
              rules={[{ required: true, message: 'Chọn thành phố' }]}
            >
              <CityAsyncSelect
                placeholder="Chọn thành phố"
                onChange={(v) => entryForm.setFieldsValue({ city_id: v, ward_id: undefined })}
              />
            </Form.Item>
            <Form.Item
              name="ward_id"
              label="Phường / xã"
              rules={[{ required: true, message: 'Chọn phường / xã' }]}
            >
              <WardAsyncSelect
                cityId={editCityId ?? ''}
                placeholder="Chọn phường / xã"
                onChange={(v) => entryForm.setFieldValue('ward_id', v)}
              />
            </Form.Item>
          </div>
          <Form.Item
            name="address"
            label="Địa chỉ chi tiết"
            rules={[{ required: true, whitespace: true, message: 'Vui lòng nhập địa chỉ' }]}
          >
            <Input placeholder="Số nhà, tên đường, toà nhà…" />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'Email không hợp lệ' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="note"
            label="Ghi chú địa điểm"
            rules={[{ max: 500, message: 'Tối đa 500 ký tự' }]}
          >
            <Input maxLength={500} placeholder="VD: Nhận hàng 8h–17h, gọi trước khi tới" />
          </Form.Item>
          <AddressReceiversField />
          <Form.Item name="is_default" valuePropName="checked" style={{ marginBottom: 0 }}>
            <Checkbox disabled={Boolean(editing?.is_default)}>Đặt làm địa chỉ mặc định</Checkbox>
          </Form.Item>
        </Form>
      ) : (
        <>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Tìm theo tên, SĐT, địa chỉ…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ marginBottom: 12 }}
          />
          <Spin spinning={isLoading}>
            {visible.length === 0 && !isLoading ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={list.length === 0 ? 'Khách chưa có địa chỉ nào' : 'Không tìm thấy địa chỉ'}
              />
            ) : (
              <Radio.Group
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                className="addr-book-list"
              >
                {visible.map((e) => (
                  <div
                    key={e.address_id}
                    className={`addr-book-item${e.address_id === selectedId ? ' is-selected' : ''}`}
                    data-testid={`addr-book-item-${e.address_id}`}
                    onClick={() => setSelectedId(e.address_id)}
                  >
                    <Radio value={e.address_id} />
                    <div className="addr-book-item-body">
                      <div className="addr-book-item-head">
                        <strong>{e.address_name || e.name}</strong>
                        {e.address_name ? <span className="addr-book-item-phone"> · {e.name}</span> : null}
                        {e.phone ? <span className="addr-book-item-phone"> · {e.phone}</span> : null}
                        {e.is_default ? (
                          <Tag color="#a4193d" style={{ marginLeft: 8 }}>
                            Mặc định
                          </Tag>
                        ) : null}
                      </div>
                      <div className="addr-book-item-addr">{formatEntryAddress(e)}</div>
                      {(e.receivers?.length ?? 0) > 0 || e.note ? (
                        <div
                          className="addr-book-item-extra"
                          style={{ display: 'flex', gap: 12, fontSize: 12, color: '#8c8c8c', minWidth: 0 }}
                        >
                          {(e.receivers?.length ?? 0) > 0 ? (
                            <span style={{ flexShrink: 0 }}>👥 +{e.receivers?.length} người nhận</span>
                          ) : null}
                          {e.note ? (
                            <span
                              title={e.note}
                              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                              📝 {e.note}
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    {canManage ? (
                      <div className="addr-book-item-actions" onClick={(ev) => ev.stopPropagation()}>
                        <Button type="link" size="small" onClick={() => startEdit(e)}>
                          Sửa
                        </Button>
                        {!e.is_default ? (
                          <Button
                            type="link"
                            size="small"
                            className="addr-book-set-default"
                            loading={setDefaultMut.isPending && setDefaultMut.variables === e.address_id}
                            onClick={() => setDefault(e)}
                          >
                            Đặt mặc định
                          </Button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ))}
              </Radio.Group>
            )}
          </Spin>
          {canManage ? (
            <Button
              block
              type="dashed"
              icon={<PlusOutlined />}
              className="addr-book-add"
              onClick={() => startEdit(null)}
            >
              Thêm địa chỉ mới
            </Button>
          ) : null}
        </>
      )}
    </Modal>
  );
}
